import React from 'react';
import { Card, Form, Select, Input, Row, Col, Space, Button, Checkbox } from 'antd';
import { PlusOutlined, MinusCircleOutlined } from '@ant-design/icons';
import { FormInstance } from 'antd/es/form';

const { Option } = Select;

interface SexualHarassmentProps {
  form: FormInstance;
}

const SexualHarassment: React.FC<SexualHarassmentProps> = ({ form }) => {
  const yesNoOptions = ['Yes', 'No'];
  const harassedByOptions = ['Supervisor', 'Coworker'];
  const whereConductOptions = [
    'Private office',
    'Work area',
    'Meeting room',
    'Public area',
    'Multiple locations'
  ];
  const bodyPartOptions = [
    'Arm', 'Hand', 'Shoulder', 'Back', 'Leg', 'Face', 'Hair', 'Other'
  ];
  const whatDoneOptions = [
    'Verbal warning',
    'Written warning',
    'Suspension',
    'Termination',
    'Transfer',
    'Training',
    'No action',
    'Other'
  ];
  const harassmentActions = [
    'Repeated suggestive comments about specific parts of your or their own body',
    'Repeated suggestive comments about your or their own clothes',
    'Repeatedly touched you over your objections',
    'Repeatedly asked you for dates over your objections',
    'Repeated suggestive words about you to others',
    'Repeated statements that you could advance in your job if you cooperated in an intimate relationship',
    'Repeated obscene gestures',
    'Repeated comments regarding his or her sex life',
    'Repeated disrobing in front of you',
    'Repeated statements that keeping your job depended on your cooperation in an intimate relationship',
    'Repeated statements that other terms or benefits of your job depended on your cooperation in an intimate relationship'
  ];
  const lossOptions = [
    'Had to take more sick time, personal time, comp time, vacation time, or time without pay',
    'Were unable to work overtime',
    'Lost advancement to a higher position',
    'Lost raises or bonuses',
    'Had no losses'
  ];

  return (
    <>
      <Card title="Sexual Harassment Actions" style={{ marginBottom: 16 }}>
        <Form.Item
          label="Select all that apply to your harassment:"
          name={['sexual_harassment_info', 'harassment_actions']}
        >
          <Checkbox.Group style={{ width: '100%' }}>
            <Row gutter={[16, 8]}>
              {harassmentActions.map((action, index) => (
                <Col span={12} key={index}>
                  <Checkbox value={action} style={{ whiteSpace: 'normal', lineHeight: '1.5' }}>
                    {action}
                  </Checkbox>
                </Col>
              ))}
            </Row>
          </Checkbox.Group>
        </Form.Item>

        <Row gutter={16}>
          <Col span={8}>
            <Form.Item
              label="Where did the offensive conduct take place?"
              name={['sexual_harassment_info', 'where_conduct_took_place']}
            >
              <Select placeholder="Select location">
                {whereConductOptions.map(option => (
                  <Option key={option} value={option}>{option}</Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              label="Was anyone else present?"
              name={['sexual_harassment_info', 'was_anyone_else_present']}
            >
              <Select placeholder="Select">
                {yesNoOptions.map(option => (
                  <Option key={option} value={option}>{option}</Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              label="What part of your body was touched?"
              name={['sexual_harassment_info', 'what_part_of_body_touched']}
            >
              <Select placeholder="Select body part">
                {bodyPartOptions.map(option => (
                  <Option key={option} value={option}>{option}</Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>
      </Card>

      <Card title="Sexual Harassment" style={{ marginBottom: 16 }}>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label="Were you sexually harassed by a supervisor or coworker?"
              name={['sexual_harassment_info', 'harassed_by']}
            >
              <Select placeholder="Select">
                {harassedByOptions.map(option => (
                  <Option key={option} value={option}>{option}</Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Card type="inner" title="List up to 3 dates you were sexually harassed:">
          <Form.List name={['sexual_harassment_info', 'harassment_dates']}>
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name, ...restField }) => (
                  <Space key={key} style={{ display: 'flex', marginBottom: 8 }} align="baseline">
                    <Form.Item
                      {...restField}
                      name={[name, 'date']}
                      label={`${name + 1}:`}
                    >
                      <Input placeholder="MMDDYYYY" />
                    </Form.Item>
                    <MinusCircleOutlined onClick={() => remove(name)} />
                  </Space>
                ))}
                {fields.length < 3 && (
                  <Form.Item>
                    <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                      Add Date
                    </Button>
                  </Form.Item>
                )}
              </>
            )}
          </Form.List>
        </Card>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label="Did you notify a supervisor?"
              name={['sexual_harassment_info', 'notified_supervisor']}
            >
              <Select placeholder="Select">
                {yesNoOptions.map(option => (
                  <Option key={option} value={option}>{option}</Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label="Date you notified a supervisor:"
              name={['sexual_harassment_info', 'date_notified_supervisor']}
            >
              <Input placeholder="MMDDYYYY" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label="Was the supervisor aware of the unwelcome attention?"
              name={['sexual_harassment_info', 'supervisor_aware_unwelcome_attention']}
            >
              <Select placeholder="Select">
                {yesNoOptions.map(option => (
                  <Option key={option} value={option}>{option}</Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label="Was action taken?"
              name={['sexual_harassment_info', 'action_taken']}
            >
              <Select placeholder="Select">
                {yesNoOptions.map(option => (
                  <Option key={option} value={option}>{option}</Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label="Date of action:"
              name={['sexual_harassment_info', 'date_of_action']}
            >
              <Input placeholder="MMDDYYYY" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label="What was done to the accused?"
              name={['sexual_harassment_info', 'what_done_to_accused']}
            >
              <Select placeholder="Select">
                {whatDoneOptions.map(option => (
                  <Option key={option} value={option}>{option}</Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label="Did the harassment stop?"
              name={['sexual_harassment_info', 'harassment_stopped']}
            >
              <Select placeholder="Select">
                {yesNoOptions.map(option => (
                  <Option key={option} value={option}>{option}</Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>
      </Card>

      <Card title="Sexual Harassment Losses" style={{ marginBottom: 16 }}>
        <Form.Item
          label="You allege that as a further result of this conduct you (select all that apply):"
          name={['sexual_harassment_info', 'harassment_losses']}
        >
          <Checkbox.Group style={{ width: '100%' }}>
            <Row gutter={[16, 8]}>
              {lossOptions.map((loss, index) => (
                <Col span={12} key={index}>
                  <Checkbox value={loss} style={{ whiteSpace: 'normal', lineHeight: '1.5' }}>
                    {loss}
                  </Checkbox>
                </Col>
              ))}
            </Row>
          </Checkbox.Group>
        </Form.Item>
      </Card>
    </>
  );
};

export default SexualHarassment;

